import { Typography } from "@mui/material";
import AllSubtractedValuesTable from "./AllSubtractedValuesTable";
import FlaggedFraudUsersTable from "./FlaggedFraudUsersTable";
import FraudCorrectionApplied from "./FraudCorrectionApplied";
import NoFraudDetected from "./NoFraudDetected";
import NoImpactField from "./NoImpactField";
import ValueCorrection from "./ValueCorrection";
import FraudNotApplicableNotice from "./FraudNotApplicableNotice";
import { useSelector } from "react-redux";
import { getDeepViewValuesEntry } from "../../deepViewFieldMap";
import { renderDeepViewText } from "../../renderDeepViewText";
import { areAmountsEqual } from "../../deepViewNumbers";

interface Props {
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
  /** Label of the column the user clicked, e.g. "Total Income (USD)" */
  columnLabel?: string;
}

const FlaggedFraudUsersTabletableHeadings = {
  user_unique_id: "User Unique ID",
  flagged_on: "Flagged On",
  flagged_by: "Flagged By",
};
const AllSubtractedValuesTabletableHeadings = {
  field: "Field",
  subtracted_amount: "Subtracted Amount",
};

// Fraud corrections are reported for these fields, in this order
const SUBTRACTED_VALUE_FIELDS = [
  "ftd",
  "qftd",
  "cpa_income",
  "revshare_income",
  "net_revenue",
  "total_income",
];

const StageFour = ({ columnKey, columnLabel }: Props) => {
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const valuesEntry = getDeepViewValuesEntry(deepViewData, columnKey);
  const isFraudClean = areAmountsEqual(
    valuesEntry?.fmt_before_fraud_usd,
    valuesEntry?.fmt_after_fraud_usd,
  );
  const AllSubtractedValuesTableRows = SUBTRACTED_VALUE_FIELDS.map((field) => ({
    field,
    subtracted_amount:
      deepViewData?.values?.[field]?.fmt_period_fraud_signed_usd,
  }));

  const fraudUsers = deepViewData?.fraud_users ?? [];
  const FlaggedFraudUsersTableRows = fraudUsers.map((user: any) => ({
    user_unique_id: user?.user_unique_id,
    flagged_on: user?.created,
    flagged_by: user?.created_by,
  }));

  // Fraud happened this period, but it did not necessarily touch this field:
  // no before-value means the field is out of scope, and an unchanged value
  // means fraud users were removed without affecting it.
  const before = valuesEntry?.fmt_before_fraud_usd;
  //const after = valuesEntry?.fmt_after_fraud_usd;
  const fraudState =
    before === null || before === undefined
      ? "NOT_APPLICABLE"
      : isFraudClean
        ? "NO_IMPACT"
        : "IMPACTED";

  if (isFraudClean) return <NoFraudDetected />;

  if (fraudState === "NOT_APPLICABLE") {
    return (
      <FraudNotApplicableNotice
        text={renderDeepViewText(valuesEntry?.stage4_prefilled)}
      />
    );
  }

  if (fraudState === "NO_IMPACT")
    return <NoImpactField columnLabel={columnLabel} />;

  return (
    <>
      <FraudCorrectionApplied columnKey={columnKey} />
      <ValueCorrection columnKey={columnKey} columnLabel={columnLabel} />
      <Typography mt={1} fontSize={"14px"} color="#00000099" fontWeight={500}>
        All Subtracted Values (raw_fraud_players_performance)
      </Typography>
      <AllSubtractedValuesTable
        tableHeadings={AllSubtractedValuesTabletableHeadings}
        rows={AllSubtractedValuesTableRows}
      />
      {FlaggedFraudUsersTableRows.length > 0 && (
        <>
          <Typography
            mb={1}
            mt={2}
            fontSize={"14px"}
            color="#00000099"
            fontWeight={500}
          >
            Fraud Users Removed This Period
          </Typography>
          <FlaggedFraudUsersTable
            tableHeadings={FlaggedFraudUsersTabletableHeadings}
            rows={FlaggedFraudUsersTableRows}
          />
        </>
      )}
    </>
  );
};

export default StageFour;
